'use client';
import {
    Document,
    Font,
    Page,
    StyleSheet,
    Text,
    View,
    Image,
} from '@react-pdf/renderer';
import Header from '../../components/pdf/Header';
import Footer from '../../components/pdf/Footer';
import dayjs from 'dayjs';
import rupee from './rupee.png';

const primaryColor = "#3778C2";
const lightGray = "#F3F6FA";
const accentColor = "#E8F4FF";

Font.register({
    family: "NotoSans",
    src: "https://fonts.gstatic.com/s/notosans/v27/o-0IIpQlx3QUlC5A4PNr6DRAW_0.woff2",
});

const styles = StyleSheet.create({
    page: {
        backgroundColor: "#fff",
        fontFamily: "NotoSans",
        fontSize: 11,
        paddingTop: 30,
        paddingLeft: 40,
        paddingRight: 40,
        lineHeight: 1.5,
        flexDirection: "column",
    },
    rupee_icon: {
        width: 10,
        height: 10,
    },
    eventTitle: {
        fontSize: 26,
        color: primaryColor,
        textAlign: "center",
        marginBottom: 6,
        fontWeight: "bold",
        textTransform: "uppercase",
    },
    subtitle: {
        textAlign: "center",
        fontSize: 12,
        color: "#555",
        marginBottom: 20,
        marginTop: 10,
    },
    vendorName: {
        textAlign: "center",
        fontSize: 14,
        color: primaryColor,
        fontWeight: "bold",
        marginBottom: 10,
        textTransform: "uppercase",
    },
    sectionHeader: {
        fontSize: 14,
        color: "#fff",
        backgroundColor: primaryColor,
        padding: 6,
        fontWeight: "bold",
        marginTop: 15,
        marginBottom: 8,
        borderRadius: 2,
    },
    categorySection: {
        borderWidth: 1,
        borderColor: primaryColor,
        borderRadius: 5,
        marginBottom: 15,
        backgroundColor: "#fff",
    },
    categoryHeader: {
        backgroundColor: primaryColor,
        padding: 8,
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
    },
    categoryTitle: {
        color: "#fff",
        fontSize: 12,
        fontWeight: "bold",
        textAlign: "center",
    },
    categoryContent: {
        padding: 10,
    },
    infoRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: 6,
    },
    infoLabel: {
        color: "#333",
        fontWeight: "bold",
        width: "40%",
    },
    infoValue: {
        color: "#555",
        width: "60%",
        textAlign: "right",
    },
    tableContainer: {
        borderWidth: 1,
        borderColor: "#ddd",
        borderRadius: 3,
        marginTop: 5,
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: lightGray,
        color: "#333",
        fontWeight: "bold",
    },
    headerCell: {
        padding: 6,
        fontSize: 10,
        borderRightWidth: 1,
        borderRightColor: "#ddd",
    },
    row: {
        flexDirection: "row",
        borderBottomWidth: 1,
        borderBottomColor: "#E5E5E5",
    },
    cell: {
        padding: 6,
        fontSize: 9,
        borderRightWidth: 1,
        borderRightColor: "#E5E5E5",
    },
    categoryTotal: {
        flexDirection: "row",
        justifyContent: "space-between",
        backgroundColor: accentColor,
        padding: 8,
        marginTop: 8,
        borderTopWidth: 1,
        borderTopColor: primaryColor,
    },
    categoryTotalLabel: {
        fontSize: 11,
        fontWeight: "bold",
        color: primaryColor,
    },
    categoryTotalAmount: {
        fontSize: 11,
        fontWeight: "bold",
        color: primaryColor,
    },
    grandTotalSection: {
        flexDirection: "row",
        backgroundColor: primaryColor,
        padding: 12,
        marginTop: 10,
        borderRadius: 3,
    },
    grandTotalLabel: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#fff",
        width: "70%",
    },
    grandTotalAmount: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#fff",
        width: "30%",
        textAlign: "right",
    },
    termsSection: {
        marginTop: 20,
        padding: 10,
        borderWidth: 1,
        borderColor: "#ddd",
        borderRadius: 3,
        backgroundColor: "#f9f9f9",
    },
    termsTitle: {
        fontSize: 12,
        fontWeight: "bold",
        color: primaryColor,
        marginBottom: 8,
        textAlign: "center",
    },
    termItem: {
        flexDirection: "row",
        alignItems: "flex-start",
        marginBottom: 4,
    },
    bullet: {
        width: 15,
        fontSize: 10,
        color: "#333",
        marginTop: 2,
    },
    termText: {
        fontSize: 9,
        color: "#444",
        flex: 1,
        lineHeight: 1.3,
        marginTop: 2,
        marginBottom: 2,
    },
    notesSection: {
        marginTop: 10,
        padding: 10,
        borderWidth: 1,
        borderColor: "#ffcccc",
        borderRadius: 3,
        backgroundColor: "#fff0f0",
    },
    notesTitle: {
        fontSize: 11,
        fontWeight: "bold",
        color: "#d63031",
        marginBottom: 5,
    },
});

const ClientQuotationPdf = ({ pdfData, mob_no_status, quotation_details, business_name }) => {

    // Fixed date parsing function
    const parseEventDates = (eventDateString) => {
        if (!eventDateString) return [];

        try {
            // Handle the format: "{\"1763632287\",\"1764409887\"}"
            const cleanedString = eventDateString.replace(/[{}"]/g, "");
            const dateStrings = cleanedString.split(',').filter(str => str.trim() !== '');

            return dateStrings.map(dateStr => {
                const timestamp = parseInt(dateStr.trim(), 10);
                // Check if timestamp is in seconds (10 digits) or milliseconds (13 digits)
                return timestamp < 10000000000 ? timestamp * 1000 : timestamp;
            });
        } catch (error) {
            console.error("Error parsing event dates:", error);
            return [];
        }
    };

    const eventTimestamps = parseEventDates(quotation_details?.event_date || "");
    const eventDates = eventTimestamps.map(timestamp => new Date(timestamp));

    // Helper functions
    const maskMobile = (number) => {
        if (!number) return "N/A";
        const cleaned = number.replace(/\D/g, '');
        if (cleaned.length <= 5) return cleaned;
        return cleaned.slice(0, 5) + '*****';
    };

    const truncateVendorName = (name) => {
        if (!name) return "VENDOR";
        if (name.length <= 5) return name;
        return name.slice(0, 5) + '...';
    };

    // FIXED: Improved calculation to handle string prices and missing quantities
    const calculateCategoryTotal = (category) => {
        const items = pdfData?.quotation_item?.filter(item => item.category === category) || [];

        return items.reduce((sum, item) => {
            // Convert price to number (handle string values)
            const price = Number(item.price) || 0;

            if (category === 'Room') {
                const roomCount = Number(item.roomCount) || 1;
                return sum + (price * roomCount);
            } else if (category === 'Menu') {
                const pax = Number(item.pax) || 1;
                return sum + (price * pax);
            } else {
                const quantity = Number(item.quantity) || 1;
                return sum + (price * quantity);
            }
        }, 0);
    };

    const roomTotal = calculateCategoryTotal('Room');
    const decorTotal = calculateCategoryTotal('Decor');
    const venueTotal = calculateCategoryTotal('Venue');
    const additionalTotal = calculateCategoryTotal('Additional');
    const menuTotal = calculateCategoryTotal('Menu');

    const grandTotal = roomTotal + decorTotal + venueTotal + additionalTotal + menuTotal;
    const updatedGrandTotal = Number(grandTotal) - (Number(pdfData?.total_discount) || 0);
    const getCategoryItems = (category) => {
        return pdfData?.quotation_item?.filter(item => item.category === category) || [];
    };

    let discountValue = 0;
    let discountLabel = "";
    if (pdfData?.discount_type === "percent") {
        discountValue = (grandTotal * pdfData?.discount_amount) / 100;
        discountLabel = `${pdfData?.discount_amount}%`;
    } else if (pdfData?.discount_type === "amounut") {
        discountValue = pdfData?.discount_amount;
        discountLabel = `₹${pdfData?.discount_amount.toLocaleString()}`;
    }

    const renderCategorySection = (category, title) => {
        const items = getCategoryItems(category);
        const total = calculateCategoryTotal(category);

        if (items.length === 0) return null;

        return (
            <View style={styles.categorySection}>
                <View style={styles.categoryHeader}>
                    <Text style={styles.categoryTitle}>{title}</Text>
                </View>
                <View style={styles.categoryContent}>
                    <View style={styles.tableContainer}>
                        <View style={styles.tableHeader}>
                            <Text style={[styles.headerCell, { width: '60%' }]}>Item Description</Text>
                            <Text style={[styles.headerCell, { width: '10%' }]}>Quantity</Text>
                            <Text style={[styles.headerCell, { width: '10%' }]}>Price</Text>
                            <Text style={[styles.headerCell, { width: '20%', borderRightWidth: 0 }]}>Amount ( <Image style={styles.rupee_icon} src={rupee} />)</Text>
                        </View>

                        {items.map((item, index) => {
                            const quantity = Number(item.quantity) || 1;
                            const price = Number(item.price) || 0;
                            const amount = price * quantity;

                            return (
                                <View key={item.id || index} style={styles.row}>
                                    <Text style={[styles.cell, { width: '60%' }]}>{item.label}</Text>
                                    <Text style={[styles.cell, { width: '10%' }]}>
                                        {quantity}
                                    </Text>
                                    <Text style={[styles.cell, { width: '10%' }]}>
                                        {price.toLocaleString()}
                                    </Text>
                                    <Text style={[styles.cell, { width: '20%', borderRightWidth: 0 }]}>
                                        {amount.toLocaleString()}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>

                    <View style={styles.categoryTotal}>
                        <Text style={styles.categoryTotalLabel}>{title} Total</Text>
                        <Text style={styles.categoryTotalAmount}>
                            <Image style={styles.rupee_icon} src={rupee} />{total.toLocaleString()}
                        </Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderRoomSection = (category, title) => {
        const items = getCategoryItems(category);
        const total = calculateCategoryTotal(category);

        if (items.length === 0) return null;

        return (
            <View style={styles.categorySection}>
                <View style={styles.categoryHeader}>
                    <Text style={styles.categoryTitle}>{title}</Text>
                </View>
                <View style={styles.categoryContent}>
                    <View style={styles.tableContainer}>
                        <View style={styles.tableHeader}>
                            <Text style={[styles.headerCell, { width: '60%' }]}>Item Description</Text>
                            <Text style={[styles.headerCell, { width: '10%' }]}>Rooms</Text>
                            <Text style={[styles.headerCell, { width: '10%' }]}>Price</Text>
                            <Text style={[styles.headerCell, { width: '20%', borderRightWidth: 0 }]}>Amount ( <Image style={styles.rupee_icon} src={rupee} />)</Text>
                        </View>

                        {items.map((item, index) => {
                            const roomCount = Number(item.roomCount) || 1;
                            const price = Number(item.price) || 0;
                            const amount = price * roomCount;

                            return (
                                <View key={item.id || index} style={styles.row}>
                                    <Text style={[styles.cell, { width: '60%' }]}>{item.label}</Text>
                                    <Text style={[styles.cell, { width: '10%' }]}>
                                        {roomCount}
                                    </Text>
                                    <Text style={[styles.cell, { width: '10%' }]}>
                                        {price.toLocaleString()}
                                    </Text>
                                    <Text style={[styles.cell, { width: '20%', borderRightWidth: 0 }]}>
                                        {amount.toLocaleString()}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>

                    <View style={styles.categoryTotal}>
                        <Text style={styles.categoryTotalLabel}>{title} Total</Text>
                        <Text style={styles.categoryTotalAmount}>
                            <Image style={styles.rupee_icon} src={rupee} />{total.toLocaleString()}
                        </Text>
                    </View>
                </View>
            </View>
        );
    };
    const renderMenuSection = (category, title) => {
        const items = getCategoryItems(category);
        const total = calculateCategoryTotal(category);

        if (items.length === 0) return null;

        return (
            <View style={styles.categorySection}>
                <View style={styles.categoryHeader}>
                    <Text style={styles.categoryTitle}>{title}</Text>
                </View>
                <View style={styles.categoryContent}>
                    <View style={styles.tableContainer}>
                        <View style={styles.tableHeader}>
                            <Text style={[styles.headerCell, { width: '60%' }]}>Item Description</Text>
                            <Text style={[styles.headerCell, { width: '10%' }]}>Pax</Text>
                            <Text style={[styles.headerCell, { width: '10%' }]}>Price</Text>
                            <Text style={[styles.headerCell, { width: '20%', borderRightWidth: 0 }]}>Amount ( <Image style={styles.rupee_icon} src={rupee} />)</Text>
                        </View>

                        {items.map((item, index) => {
                            const pax = Number(item.pax) || 1;
                            const price = Number(item.price) || 0;
                            const amount = price * pax;

                            return (
                                <View key={item.id || index} style={styles.row}>
                                    <Text style={[styles.cell, { width: '60%' }]}>{item.label}</Text>
                                    <Text style={[styles.cell, { width: '10%' }]}>
                                        {pax}
                                    </Text>
                                    <Text style={[styles.cell, { width: '10%' }]}>
                                        {price.toLocaleString()}
                                    </Text>
                                    <Text style={[styles.cell, { width: '20%', borderRightWidth: 0 }]}>
                                        {amount.toLocaleString()}
                                    </Text>
                                </View>
                            );
                        })}
                    </View>
                    <View style={styles.categoryTotal}>
                        <Text style={styles.categoryTotalLabel}>{title} Total</Text>
                        <Text style={styles.categoryTotalAmount}>
                            <Image style={styles.rupee_icon} src={rupee} />{total.toLocaleString()}
                        </Text>
                    </View>
                </View>
            </View>
        );
    };
    const getSelectedServices = () => {
        return quotation_details?.service_option
            ?.filter(service => service.isSelected)
            ?.map(service => service.name) || [];
    };
    const contentHeight = Math.max(842, quotation_details?.length * 300);
    return (
        <Document>
            <Page size={[595.28, contentHeight]} style={styles.page} wrap>
                <Text style={styles.eventTitle}>Event Planet</Text>
                <Header />

                {/* Client Details */}
                <Text style={styles.sectionHeader}>Client Details</Text>
                <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Contact Person:</Text>
                    <Text style={styles.infoValue}>{quotation_details?.name || "N/A"}</Text>
                </View>
                <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Mobile Number:</Text>
                    <Text style={styles.infoValue}>
                        {mob_no_status ? quotation_details?.mob_no : maskMobile(quotation_details?.mob_no)}
                    </Text>
                </View>
                <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Groom Name:</Text>
                    <Text style={styles.infoValue}>{quotation_details?.groom_name || "N/A"}</Text>
                </View>
                <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Bride Name:</Text>
                    <Text style={styles.infoValue}>{quotation_details?.bride_name || "N/A"}</Text>
                </View>
                <View style={styles.infoRow}>
                    <Text style={styles.infoLabel}>Event Manage by:</Text>
                    <Text style={styles.infoValue}>{truncateVendorName(business_name)}</Text>
                </View>

                {/* Event Details from Quotation */}
                {quotation_details && (
                    <>
                        <Text style={styles.sectionHeader}>Event Details</Text>
                        <View style={styles.tableContainer}>
                            <View style={styles.tableHeader}>
                                <Text style={[styles.headerCell, { width: '30%', borderRightWidth: 0 }]}>Event Information</Text>
                                <Text style={[styles.headerCell, { width: '70%', borderRightWidth: 0 }]}>Details</Text>
                            </View>

                            {/* Event Types & Pax */}
                            {quotation_details?.event_options?.filter(e => e.isSelected).length > 0 && (
                                <View style={styles.row}>
                                    <Text style={[styles.cell, { width: '30%', borderRightWidth: 0 }]}>Event Types & Pax</Text>
                                    <Text style={[styles.cell, { width: '70%', borderRightWidth: 0 }]}>
                                        {quotation_details.event_options
                                            .filter(e => e.isSelected)
                                            .map((e, i) => `${e.name} (${e.pax || 0} Pax)`)
                                            .join(", ")}
                                    </Text>
                                </View>
                            )}

                            {/* Event Dates - Fixed */}
                            {eventDates.length > 0 && (
                                <View style={styles.row}>
                                    <Text style={[styles.cell, { width: '30%', borderRightWidth: 0 }]}>Event Dates</Text>
                                    <Text style={[styles.cell, { width: '70%', borderRightWidth: 0 }]}>
                                        {eventDates.map((date, i) =>
                                            dayjs(date).format("DD MMM, YYYY") +
                                            (i !== eventDates.length - 1 ? ", " : "")
                                        )}
                                    </Text>
                                </View>
                            )}

                            {/* Food Type */}
                            {quotation_details?.food_type && (
                                <View style={styles.row}>
                                    <Text style={[styles.cell, { width: '30%', borderRightWidth: 0 }]}>Food Type</Text>
                                    <Text style={[styles.cell, { width: '70%', borderRightWidth: 0 }]}>
                                        {quotation_details.food_type}
                                    </Text>
                                </View>
                            )}

                            {/* Venue Type */}
                            {quotation_details?.venue_options?.filter(v => v.isSelected).length > 0 && (
                                <View style={styles.row}>
                                    <Text style={[styles.cell, { width: '30%', borderRightWidth: 0 }]}>Venue Type</Text>
                                    <Text style={[styles.cell, { width: '70%', borderRightWidth: 0 }]}>
                                        {quotation_details.venue_options
                                            .filter(v => v.isSelected)
                                            .map(v => v.name)
                                            .join(", ")}
                                    </Text>
                                </View>
                            )}

                            {/* Additional Services - Fixed */}
                            {getSelectedServices().length > 0 && (
                                <View style={styles.row}>
                                    <Text style={[styles.cell, { width: '30%', borderRightWidth: 0 }]}>Additional Services</Text>
                                    <Text style={[styles.cell, { width: '70%', borderRightWidth: 0 }]}>
                                        {getSelectedServices().join(", ")}
                                    </Text>
                                </View>
                            )}

                            {/* Number of Rooms */}
                            {quotation_details?.no_of_room && (
                                <View style={styles.row}>
                                    <Text style={[styles.cell, { width: '30%', borderRightWidth: 0 }]}>Number of Rooms</Text>
                                    <Text style={[styles.cell, { width: '70%', borderRightWidth: 0 }]}>
                                        {quotation_details.no_of_room}
                                    </Text>
                                </View>
                            )}

                            {/* Decor Theme */}
                            {quotation_details?.decor_theme?.label && (
                                <View style={styles.row}>
                                    <Text style={[styles.cell, { width: '30%', borderRightWidth: 0 }]}>Decor Theme</Text>
                                    <Text style={[styles.cell, { width: '70%', borderRightWidth: 0 }]}>
                                        {quotation_details.decor_theme.label}
                                    </Text>
                                </View>
                            )}
                        </View>
                    </>
                )}

                {/* Quotation Items - Category Wise Sections */}
                {/* FIXED: Always show the section header and let individual sections handle their visibility */}
                <Text style={styles.sectionHeader}>Quotation Breakdown</Text>

                {/* Rooms Section */}
                {renderRoomSection('Room', 'ROOMS')}

                {/* Decor Section */}
                {renderMenuSection('Menu', quotation_details?.food_type)}

                {renderCategorySection('Decor', 'DECOR & THEMING')}

                {/* Venue Section */}
                {renderCategorySection('Venue', 'VENUE SERVICES')}

                {/* Additional Services Section */}
                {renderCategorySection('Additional', 'ADDITIONAL SERVICES')}

                {(pdfData?.discount_type !== "") && (
                    <View style={styles.grandTotalSection}>
                        <Text style={styles.grandTotalLabel}>
                            Discount ({discountLabel || pdfData?.discount_amount})
                        </Text>

                        <Text style={styles.grandTotalAmount}>
                            - ₹{discountValue  || pdfData?.discount_amount}
                        </Text>
                    </View>
                )}

                {(pdfData?.quotation_item?.length > 0) && (
                    <View style={styles.grandTotalSection}>
                        <Text style={styles.grandTotalLabel}>GRAND TOTAL</Text>
                        <Text style={styles.grandTotalAmount}>₹{updatedGrandTotal.toLocaleString()}</Text>
                    </View>
                )}

                {/* Notes */}
                {pdfData?.notes && (
                    <View style={styles.notesSection}>
                        <Text style={styles.notesTitle}>SPECIAL NOTES</Text>
                        <Text style={styles.termText}>{pdfData.notes}</Text>
                    </View>
                )}

                {/* Terms and Conditions */}
                <View style={styles.termsSection}>
                    <Text style={styles.termsTitle}>TERMS & CONDITIONS</Text>

                    <View style={styles.termItem}>
                        <Text style={styles.bullet}>1</Text>
                        <Text style={styles.termText}>
                            The quoted prices are valid for the next 7 days only from the date of issue.
                        </Text>
                    </View>

                    <View style={styles.termItem}>
                        <Text style={styles.bullet}>2</Text>
                        <Text style={styles.termText}>
                            Bookings will be confirmed strictly on a first-come, first-served basis.
                        </Text>
                    </View>

                    <View style={styles.termItem}>
                        <Text style={styles.bullet}>3</Text>
                        <Text style={styles.termText}>
                            All listed services will be delivered by our authorized Event Partners.
                        </Text>
                    </View>

                    <View style={styles.termItem}>
                        <Text style={styles.bullet}>4</Text>
                        <Text style={styles.termText}>
                            Prices may vary based on event-specific requirements or customizations.
                        </Text>
                    </View>

                    <View style={styles.termItem}>
                        <Text style={styles.bullet}>5</Text>
                        <Text style={styles.termText}>
                            Applicable taxes will be charged as per current government regulations.
                        </Text>
                    </View>

                    <View style={styles.termItem}>
                        <Text style={styles.bullet}>6</Text>
                        <Text style={styles.termText}>
                            The use or service of alcohol will be subject to venue policies, local government laws and permissions.
                        </Text>
                    </View>
                    <View style={styles.termItem}>
                        <Text style={styles.bullet}>7</Text>
                        <Text style={styles.termText}>
                            The rates offered are exclusive B2B prices and must not be shared, published, or disclosed to any third party under any circumstances.
                        </Text>
                    </View>
                </View>

                {/* Footer */}
                <Footer />
            </Page>
        </Document>
    );
};

export default ClientQuotationPdf;